import type {Attendee, Context} from './attendee-container-machine';
import type {VerifyGuest, VerifyGuestVariables} from './gql';
import {VerifyGuestMutation} from './gql';

type VerifyAttendeProps = {
  /** Details provided by the browser client */
  context: Context;
  /** The id of the show to verify */
  environmentId: string;
  /** The published version of the site to verify against */
  siteVersionId?: string;
  /** The access code to verify */
  accessCode: string;
};

/**
 * Verify that an Attendee with the context and access code provided exists in a
 * particular show. Thrown errors will be caught by the state machine.
 * @returns details about the attendee for the verified access code
 * @throws when errors returned by GraphQL
 * @throws when no data from the `verifyAccessCode` mutation
 */
export async function verifyAccessCode({
  context,
  environmentId,
  accessCode,
}: VerifyAttendeProps): Promise<Attendee> {
  return context.client
    .mutate<VerifyGuest, VerifyGuestVariables>({
      mutation: VerifyGuestMutation,
      variables: {environmentId, accessCode},
      context: {showId: context.showId},
    })
    .then((result) => {
      const {errors, data} = result;

      // if an error message was returned, throw it
      if (typeof errors !== 'undefined') {
        throw new Error(errors.map((e) => e.message).join('  '));
      }
      // if no verification data was returned, throw an error
      else if (
        typeof data?.verifyGuest.sessionToken === 'undefined' ||
        typeof data?.verifyGuest.attendee === 'undefined' ||
        data.verifyGuest.sessionToken === null ||
        data.verifyGuest.attendee === null
      ) {
        throw new Error('No data from verify.');
      }
      // otherwise, return the attendee's data
      else {
        const result: Attendee = {
          ...data.verifyGuest.attendee,
          sessionToken: data.verifyGuest.sessionToken,
          attendeeTags: data.verifyGuest.attendeeTags,
          avatar: data.verifyGuest.avatar ?? undefined,
          chatTokens: data.verifyGuest.chatTokens,
          attendeeType: 'access-code',
        };
        return result;
      }
    })
    .catch((reason) => {
      if (reason instanceof Error && reason.message.includes('has been used')) {
        throw new AccessCodeOverusedError(reason.message);
      } else if (
        reason instanceof Error &&
        (reason.message.startsWith('No Access Codes match') ||
          reason.message.startsWith('No matching Access Codes'))
      ) {
        throw new AccessCodeNotFoundError(reason.message);
      }
      throw reason;
    });
}

/**
 * Custom `Error` implementation indicating access code was used too many times.
 */
export class AccessCodeOverusedError extends Error {
  constructor(m: string) {
    super(m);
    // Set the prototype explicitly
    // See https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#generated-constructor-code-substitutes-the-return-value-of-super-calls-as-this
    Object.setPrototypeOf(this, AccessCodeOverusedError.prototype);
  }
}

/**
 * Custom `Error` implementation indicating the access code did not match.
 */
export class AccessCodeNotFoundError extends Error {
  constructor(m: string) {
    super(m);
    // Set the prototype explicitly
    // See https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#generated-constructor-code-substitutes-the-return-value-of-super-calls-as-this
    Object.setPrototypeOf(this, AccessCodeNotFoundError.prototype);
  }
}
